import {disableGatewayOption, observeCheckoutHTML} from '../../checkout';
import {createPaymentRequestOptions, updatePaymentRequestOptions} from '../payment-request';
import {AbstractStripePayment, type ConfirmResult, type TokenResult} from './class-abstract-stripe-payment';
import {type Stripe, type StripeElements, type PaymentRequest} from '@stripe/stripe-js';
import {createSetupIntent} from '../setup-intent';
import {getLocaleText} from '../../../../express-checkout/ts/util/translation';

abstract class AbstractStripePaymentRequestPayment extends AbstractStripePayment {
	private static tokenizePaymentRequest(paymentRequest: PaymentRequest): Promise<TokenResult> | TokenResult {
		return new Promise(resolve => {
			paymentRequest.once('paymentmethod', event => {
				paymentRequest.off('cancel');

				event.complete('success');
				resolve([event.paymentMethod.id, {}]);
			});

			paymentRequest.once('cancel', () => {
				paymentRequest.off('paymentmethod');

				resolve([null, {
					note: 'Payment was canceled by customer',
				}]);
			});

			paymentRequest.show();
		});
	}

	private paymentRequest: PaymentRequest | null;

	constructor(stripe: Stripe, elements: StripeElements) {
		super(stripe, elements);

		this.paymentRequest = null;
	}

	/**
     * Initializes the paymentRequest object. Every when the payment class is initialized and after any attempt so every attempt has its own paymentRequest object(This allows tokenization to happen like all other payment methods).
     */
	public override async init(): Promise<void> {
		try {
			this.paymentRequest = this.stripe.paymentRequest(createPaymentRequestOptions());

			const result = await this.paymentRequest.canMakePayment();

			if (this.paymentRequestType() === 'googlePay' && !result?.['googlePay']) {
				observeCheckoutHTML(disableGatewayOption(this.gatewayId()));
				disableGatewayOption(this.gatewayId())();
				return;
			}

			if (this.paymentRequestType() === 'applePay' && !result?.['applePay']) {
				observeCheckoutHTML(disableGatewayOption(this.gatewayId()));
				disableGatewayOption(this.gatewayId())();
				return;
			}
		} catch (error) {
			console.error(error);
		}
	}

	public async tokenize(): Promise<TokenResult> {
		if (!this.paymentRequest) {
			const message = this.paymentRequestType() === 'googlePay'
				? getLocaleText('Sorry Google Pay is not available right now. Please use another payment method or try again later.')
				: getLocaleText('Sorry Apple Pay is not available right now. Please use another payment method or try again later.');

			return [null, {
				paymentStatus: 'creationfailed',
				note: message,
			}];
		}

		this.paymentRequest.update(updatePaymentRequestOptions());

		const [paymentMethodId, update] = await AbstractStripePaymentRequestPayment.tokenizePaymentRequest(this.paymentRequest);
		if (!paymentMethodId) {
			return [null, update];
		}

		const clientSecret = await createSetupIntent('card', paymentMethodId);
		if (!clientSecret) {
			return [null, {
				paymentStatus: 'creationfailed',
				note: 'Failed to create setup intent.',
			}];
		}

		const {error: confirmError, setupIntent: confirmIntent} = await this.stripe.confirmCardSetup(clientSecret);
		if (!confirmIntent || confirmError) {
			return [null, {
				paymentStatus: 'creationfailed',
				note: confirmError?.message ?? 'Failed to confirm setup intent.',
			}];
		}

		if (confirmIntent.status !== 'succeeded') {
			return [null, {
				paymentStatus: 'creationfailed',
				note: `Setup intent resulted in invalid status(${confirmIntent.status})`,
			}];
		}

		return [confirmIntent.payment_method as string, {}];
	}

	public async confirm(clientSecret: string): Promise<ConfirmResult> {
		const {paymentIntent, error} = await this.stripe.retrievePaymentIntent(clientSecret);

		if (!paymentIntent || error) {
			const message = error?.message ?? 'Failed to retrieve payment intent.';
			return [null, {
				note: message,
			}];
		}

		if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
			return ['success', {}];
		}

		if (paymentIntent.status === 'requires_action') {
			const {paymentIntent, error} = await this.stripe.confirmCardPayment(clientSecret);
			if (!paymentIntent || error) {
				const message = error?.message ?? 'Failed to confirm payment intent.';
				return [null, {
					note: message,
				}];
			}

			if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
				return ['success', {}];
			}
		}

		return [null, {
			note: `Failed to confirm payment intent with status(${paymentIntent.status})`,
		}];
	}

	protected abstract paymentRequestType(): 'googlePay' | 'applePay';
}

export {
	AbstractStripePaymentRequestPayment,
};
